import { useState } from 'react';

import { colour, space, text } from './theme';
import { ToolIcon } from './toolIcons';

/**
 * Tool palette and history controls.
 *
 * Presentation only: it renders the list the controller advertises and reports clicks. It
 * has no idea what a tool does, which is what kept adding twenty-eight of them in M11
 * a change in the input layer alone.
 *
 * ## Collapsing
 *
 * The palette can fold down to a strip of icons. On a laptop the expanded panel eats a
 * sixth of the canvas width, and a geometry diagram wants every pixel — but a bare icon
 * strip is unusable for someone meeting the tools for the first time, so the expanded form
 * is the default and collapsing is the deliberate choice.
 *
 * ## Grouping
 *
 * Sections come from the tools themselves. This became necessary rather than decorative at
 * M11: forty-nine tools in a flat list are taller than the viewport. Only one section is
 * open at a time — an accordion, not a tree — because two open sections already overflow a
 * laptop screen, which is the problem the grouping exists to solve.
 *
 * The section holding the active tool opens itself *when the tool changes* — a keyboard
 * shortcut can switch tools without touching the palette, and a palette still pointing at
 * the previous section would be showing something false.
 *
 * "When it changes" is the whole subtlety. The first version derived the open section from
 * the active tool on every render, which meant the section holding the current tool always
 * won and clicking any other heading did nothing at all: the Solids group was unreachable
 * except by its keyboard shortcut. The open section is therefore state that the active tool
 * *nudges* on change, not a value derived from it.
 *
 * ## What the selected tool looks like, and why it changed
 *
 * It was a saturated blue block with white text. One of those is a button; a list of them
 * with one filled in is a *toolbar*, and the eye reads the whole column as chrome. With 49
 * tools down the left of a drawing that is a great deal of colour arguing with the figure.
 *
 * It is now a soft tint, a blue label, and a short rail down the left edge of the row — the
 * `es-row--on` treatment shared with the object list. The rail is the part that does the work:
 * a tint alone at this contrast is easy to miss when scanning, and raising the tint until it
 * cannot be missed is how the palette became a wall of blue in the first place.
 *
 * ## Why every row carries a drawing of itself
 *
 * The collapsed strip used to show each tool's *shortcut character* as its icon, on the
 * reasoning that the letter is already what the user presses. It is, and it says nothing about
 * what the tool does: a column of `m M e a B A T j x` is a column of letters. Worse, the
 * expanded list leaned entirely on English words — `Segment`, `Ray`, `Frustum` — in an
 * interface whose users are Vietnamese schoolteachers.
 *
 * Both are fixed by the same thing. Each row now shows a miniature of what the tool *makes*
 * (see `toolIcons.tsx`), so the palette can be read without reading, and the labels are in the
 * language of the rest of the application.
 */

export interface ToolbarEntry {
  readonly id: string;
  readonly label: string;
  readonly shortcut?: string;
  /** Section heading. Tools without one are gathered under "Tools". */
  readonly group?: string;
}

export interface HistoryControls {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
}

export interface ToolbarProps {
  readonly tools: readonly ToolbarEntry[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  readonly history: HistoryControls;
}

export function Toolbar({ tools, activeId, onSelect, history }: ToolbarProps) {
  const [collapsed, setCollapsed] = useState(false);

  const groups = groupTools(tools);
  const activeGroup = groups.find((group) => group.tools.some((tool) => tool.id === activeId));

  const [open, setOpen] = useState<string | null>(groups[0]?.name ?? null);
  const [seenTool, setSeenTool] = useState(activeId);

  // Adjusting state during render, which React documents for exactly this case: reacting to
  // a changed prop without an effect and without a second paint.
  if (activeId !== seenTool) {
    setSeenTool(activeId);
    if (activeGroup !== undefined) setOpen(activeGroup.name);
  }

  return (
    <div
      className="es-panel es-scroll"
      style={{
        position: 'absolute',
        left: space.wide,
        top: space.wide,
        maxHeight: 'calc(100vh - 24px)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: space.hair,
        padding: space.snug,
      }}
    >
      <button
        type="button"
        className="es-btn es-btn--ghost"
        onClick={() => {
          setCollapsed((value) => !value);
        }}
        title={collapsed ? 'Hiện tên công cụ' : 'Thu gọn thanh công cụ'}
        aria-expanded={!collapsed}
        style={{
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: '3px 7px',
          font: text.micro,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: colour.faint,
        }}
      >
        {!collapsed && <span>Công cụ</span>}
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>
          {collapsed ? '»' : '«'}
        </span>
      </button>

      <div style={{ display: 'flex', gap: space.tight }}>
        <HistoryButton
          label={collapsed ? '↶' : 'Hoàn tác'}
          shortcut={collapsed ? '' : 'Ctrl Z'}
          enabled={history.canUndo}
          title={
            history.undoLabel === null ? 'Không có gì để hoàn tác' : `Hoàn tác ${history.undoLabel}`
          }
          onClick={history.onUndo}
        />
        <HistoryButton
          label={collapsed ? '↷' : 'Làm lại'}
          shortcut={collapsed ? '' : 'Shift Z'}
          enabled={history.canRedo}
          title={
            history.redoLabel === null ? 'Không có gì để làm lại' : `Làm lại ${history.redoLabel}`
          }
          onClick={history.onRedo}
        />
      </div>

      <div className="es-divider" style={{ height: 1, margin: `${String(space.tight)}px 0` }} />

      {groups.map((group) => {
        const expanded = collapsed || groups.length === 1 || group.name === open;
        return (
          <div key={group.name} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {!collapsed && groups.length > 1 && (
              <button
                type="button"
                className="es-eyebrow"
                onClick={() => {
                  setOpen(group.name === open ? null : group.name);
                }}
                aria-expanded={expanded}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 6px 2px',
                  marginTop: space.tight,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span>{group.name}</span>
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    fontSize: 8,
                    transform: expanded ? 'none' : 'rotate(-90deg)',
                    transition: 'transform var(--es-quick)',
                  }}
                >
                  ▼
                </span>
              </button>
            )}

            {expanded &&
              group.tools.map((tool) => (
                <ToolButton
                  key={tool.id}
                  tool={tool}
                  active={tool.id === activeId}
                  collapsed={collapsed}
                  onSelect={onSelect}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}

function ToolButton({
  tool,
  active,
  collapsed,
  onSelect,
}: {
  readonly tool: ToolbarEntry;
  readonly active: boolean;
  readonly collapsed: boolean;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'es-row es-row--on' : 'es-row'}
      aria-pressed={active}
      onClick={() => {
        onSelect(tool.id);
      }}
      title={tool.shortcut === undefined ? tool.label : `${tool.label} (${tool.shortcut})`}
      style={{
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 9,
        minWidth: collapsed ? 32 : 186,
        padding: collapsed ? '5px 4px' : '5px 10px',
        textAlign: collapsed ? 'center' : 'left',
      }}
    >
      {collapsed ? (
        /*
         * The glyph alone, with the shortcut kept in the tooltip.
         *
         * It used to be the shortcut *character*, on the reasoning that the letter is already
         * what the user presses. True, and it tells them nothing about what the tool does —
         * forty-nine rows of single letters is a column nobody can scan.
         */
        <ToolIcon tool={tool.id} size={16} />
      ) : (
        <>
          <ToolIcon tool={tool.id} />
          <span style={{ flex: 1 }}>{tool.label}</span>
          {tool.shortcut !== undefined && <kbd className="es-kbd">{tool.shortcut}</kbd>}
        </>
      )}
    </button>
  );
}

/**
 * An undo or redo button.
 *
 * Disabled rather than hidden when unavailable: a control that disappears makes the
 * toolbar reflow and costs the user the muscle memory of where it was.
 */
function HistoryButton({
  label,
  shortcut,
  enabled,
  title,
  onClick,
}: {
  readonly label: string;
  readonly shortcut: string;
  readonly enabled: boolean;
  readonly title: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="es-btn"
      onClick={onClick}
      disabled={!enabled}
      title={title}
      style={{
        flex: 1,
        justifyContent: shortcut === '' ? 'center' : 'space-between',
        padding: '4px 8px',
        font: text.small,
      }}
    >
      <span>{label}</span>
      {shortcut !== '' && <kbd className="es-kbd">{shortcut}</kbd>}
    </button>
  );
}

interface ToolGroup {
  readonly name: string;
  readonly tools: readonly ToolbarEntry[];
}

/** Groups tools in declaration order, so the palette mirrors the registry. */
function groupTools(tools: readonly ToolbarEntry[]): readonly ToolGroup[] {
  const order: string[] = [];
  const byName = new Map<string, ToolbarEntry[]>();

  for (const tool of tools) {
    const name = tool.group ?? 'Công cụ';
    const existing = byName.get(name);
    if (existing === undefined) {
      order.push(name);
      byName.set(name, [tool]);
    } else {
      existing.push(tool);
    }
  }

  return order.map((name) => ({ name, tools: byName.get(name) ?? [] }));
}
